import {
  autoUpdate,
  FloatingFocusManager,
  FloatingPortal,
  flip,
  offset,
  shift,
  size,
  useFloating,
} from "@floating-ui/react";
import type { QueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { NavigateFunction } from "react-router-dom";
import { useNavigate } from "react-router-dom";

import { queryClient } from "@src/queryClient";
import { Button } from "@src/shared/components/ui/button";
import { cn } from "@src/shared/lib/utils";

import {
  TourGuideEntity,
  type TourGuideEvent,
  type TourGuideStepActionVisibilityEvent,
  type TourGuideStepEventActionConfig,
  type TourId,
  type TourPlacement,
  type TourStepId,
  tourGuideBus,
  useTourGuideStore,
} from "../../domain";

const useIsomorphicLayoutEffect =
  typeof window === "undefined" ? useEffect : useLayoutEffect;

export interface TourStepConfig {
  id: TourStepId;
  title: string;
  description: ReactNode;
  placement?: TourPlacement;
  action?: TourStepActions;
  actions?: Record<string, TourGuideStepEventActionConfig>;
  completion?: TourStepCompletion;
}

export interface TourStepCompletion {
  event: string;
  autoAdvance?: boolean;
  instructions?: ReactNode;
  matches?: (event: TourGuideEvent) => boolean;
}

export type TourStepActionTrigger =
  | "onEnter"
  | "onNext"
  | "onPrevious"
  | "onSkip"
  | "onComplete";

export interface TourStepActionContext {
  navigate: NavigateFunction;
  queryClient: QueryClient;
  tourId: TourId;
  step: TourStepConfig;
  currentIndex: number;
  totalSteps: number;
}

export type TourStepActionCleanup = () => void | Promise<void>;
export type TourStepActionResult = void | TourStepActionCleanup;

export type TourStepAction = (
  context: TourStepActionContext,
) => TourStepActionResult | Promise<TourStepActionResult>;

export interface TourStepActionConfig {
  callback: TourStepAction;
  hideUI?: boolean;
}

export type TourStepActions = Partial<
  Record<TourStepActionTrigger, TourStepActionConfig>
>;

export interface TourProps {
  id: TourId;
  steps: readonly TourStepConfig[];
  className?: string;
  defaultPlacement?: TourPlacement;
  popoverOffset?: number;
  portalRoot?: HTMLElement | null;
  spotlightPadding?: number;
}

interface TourPopoverProps {
  tourId: TourId;
  step: TourStepConfig;
  currentIndex: number;
  totalSteps: number;
  previousStepId: TourStepId | null;
  nextStepId: TourStepId | null;
  targetEl: HTMLElement | null;
  isStepInitiallyComplete: boolean;
  shouldSkipEnterAction: boolean;
  onStepComplete: (stepId: TourStepId) => void;
  onAllActionCleanups: () => Promise<void>;
  onConsumeSkippedEnterAction: (stepId: TourStepId) => void;
  onRegisterActionCleanup: (
    stepId: TourStepId,
    cleanup: TourStepActionCleanup,
  ) => void;
  onStepActionCleanups: (stepId: TourStepId) => Promise<void>;
  onSuppressEnterAction: (stepId: TourStepId) => void;
  className?: string;
  defaultPlacement: TourPlacement;
  popoverOffset: number;
  portalRoot?: HTMLElement | null;
  spotlightPadding: number;
}

interface TargetRect {
  targetEl: HTMLElement;
  top: number;
  left: number;
  width: number;
  height: number;
}

interface TourStepContentProps {
  step: TourStepConfig;
  currentIndex: number;
  totalSteps: number;
  titleId: string;
  descriptionId: string;
  targetMissing?: boolean;
  completionInstructions?: ReactNode;
  isActionPending: boolean;
  isNextDisabled: boolean;
  onBack: () => void;
  onNext: () => void;
  onSkip: () => void;
}

function useTargetRect(targetEl: HTMLElement | null): TargetRect | null {
  const [rect, setRect] = useState<TargetRect | null>(null);

  useIsomorphicLayoutEffect(() => {
    if (!targetEl || typeof window === "undefined") {
      setRect(null);
      return;
    }

    const updateRect = () => {
      const nextRect = targetEl.getBoundingClientRect();

      setRect({
        targetEl,
        top: nextRect.top,
        left: nextRect.left,
        width: nextRect.width,
        height: nextRect.height,
      });
    };

    updateRect();

    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(updateRect);
    resizeObserver?.observe(targetEl);
    window.addEventListener("resize", updateRect);
    window.addEventListener("scroll", updateRect, true);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateRect);
      window.removeEventListener("scroll", updateRect, true);
    };
  }, [targetEl]);

  return rect?.targetEl === targetEl ? rect : null;
}

function TourStepContent({
  step,
  currentIndex,
  totalSteps,
  titleId,
  descriptionId,
  targetMissing = false,
  completionInstructions,
  isActionPending,
  isNextDisabled,
  onBack,
  onNext,
  onSkip,
}: TourStepContentProps) {
  const isFirstStep = currentIndex === 0;
  const isLastStep = currentIndex === totalSteps - 1;

  return (
    <>
      <div className="flex flex-col gap-1">
        <p
          aria-atomic="true"
          aria-live="polite"
          className="text-xs font-medium text-muted-foreground"
          role="status"
        >
          Step {currentIndex + 1} of {totalSteps}
        </p>
        <h2
          className="text-sm font-semibold leading-none text-popover-foreground"
          id={titleId}
        >
          {step.title}
        </h2>
      </div>
      <div
        className="flex flex-col gap-2 text-sm text-muted-foreground"
        id={descriptionId}
      >
        <div>{step.description}</div>
        {targetMissing && (
          <p>
            The target for this tour step is not visible. You can continue the
            tour or skip it.
          </p>
        )}
        {completionInstructions && (
          <p className="text-xs font-medium text-primary">
            {completionInstructions}
          </p>
        )}
      </div>
      <div className="flex items-center justify-between gap-2 pt-1">
        <Button
          disabled={isActionPending}
          onClick={onSkip}
          size="sm"
          type="button"
          variant="ghost"
        >
          Skip
        </Button>
        <div className="flex items-center gap-2">
          {!isFirstStep && (
            <Button
              disabled={isActionPending}
              onClick={onBack}
              size="sm"
              type="button"
              variant="outline"
            >
              Back
            </Button>
          )}
          <Button
            disabled={isActionPending || isNextDisabled}
            onClick={onNext}
            size="sm"
            type="button"
          >
            {isLastStep ? "Finish tour" : "Next"}
          </Button>
        </div>
      </div>
    </>
  );
}

function TourPopover({
  tourId,
  step,
  currentIndex,
  totalSteps,
  previousStepId,
  nextStepId,
  targetEl,
  isStepInitiallyComplete,
  shouldSkipEnterAction,
  onStepComplete,
  onAllActionCleanups,
  onConsumeSkippedEnterAction,
  onRegisterActionCleanup,
  onStepActionCleanups,
  onSuppressEnterAction,
  className,
  defaultPlacement,
  popoverOffset,
  portalRoot,
  spotlightPadding,
}: TourPopoverProps) {
  const titleId = useId();
  const descriptionId = useId();
  const navigate = useNavigate();
  const stopTour = useTourGuideStore((state) => state.stopTour);
  const nextStep = useTourGuideStore((state) => state.nextStep);
  const previousStep = useTourGuideStore((state) => state.previousStep);
  const fallbackDialogRef = useRef<HTMLDivElement>(null);
  const [isActionPending, setIsActionPending] = useState(false);
  const isActionPendingRef = useRef(false);
  const isSkipRequestedRef = useRef(false);
  const [hiddenActionTrigger, setHiddenActionTrigger] =
    useState<TourStepActionTrigger | null>(null);
  const [hiddenStepActionName, setHiddenStepActionName] = useState<
    string | null
  >(null);
  const [isStepComplete, setIsStepComplete] = useState(
    !step.completion || isStepInitiallyComplete,
  );
  const rect = useTargetRect(targetEl);
  const hasTarget = Boolean(targetEl);
  const { refs, floatingStyles, context } = useFloating({
    elements: {
      reference: targetEl,
    },
    middleware: [
      offset(popoverOffset),
      flip({ padding: 16, fallbackStrategy: "bestFit" }),
      shift({ padding: 16, crossAxis: true }),
      size({
        padding: 16,
        apply({ availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${Math.max(160, availableHeight)}px`,
            overflowY: "auto",
          });
        },
      }),
    ],
    open: hasTarget,
    placement: step.placement ?? defaultPlacement,
    strategy: "fixed",
    whileElementsMounted: autoUpdate,
  });

  const setActionPending = useCallback((pending: boolean) => {
    isActionPendingRef.current = pending;
    setIsActionPending(pending);
  }, []);

  const runStepAction = useCallback(
    async (trigger: TourStepActionTrigger) => {
      const actionConfig = step.action?.[trigger];

      if (!actionConfig) {
        return;
      }

      return actionConfig.callback({
        currentIndex,
        navigate,
        queryClient,
        step,
        totalSteps,
        tourId,
      });
    },
    [currentIndex, navigate, step, totalSteps, tourId],
  );

  const runStepActionAndRegisterCleanup = useCallback(
    async (trigger: TourStepActionTrigger) => {
      const cleanup = await runStepAction(trigger);
      if (typeof cleanup === "function") {
        onRegisterActionCleanup(
          trigger === "onNext" && nextStepId ? nextStepId : step.id,
          cleanup,
        );
      }
    },
    [nextStepId, onRegisterActionCleanup, runStepAction, step.id],
  );

  const runPendingFlow = useCallback(
    async (flow: () => Promise<void>) => {
      if (isActionPendingRef.current) {
        return;
      }

      setActionPending(true);

      try {
        await flow();
      } finally {
        setActionPending(false);
      }
    },
    [setActionPending],
  );

  const runStepActionWithVisibility = useCallback(
    async (
      trigger: TourStepActionTrigger,
      options: { managePending?: boolean } = {},
    ) => {
      const shouldHideTour = step.action?.[trigger]?.hideUI === true;
      const managePending = options.managePending ?? true;

      if (managePending) {
        if (isActionPendingRef.current) {
          return;
        }

        setActionPending(true);
      }
      setHiddenActionTrigger(shouldHideTour ? trigger : null);

      try {
        await runStepActionAndRegisterCleanup(trigger);
      } finally {
        if (managePending) {
          setActionPending(false);
        }
        setHiddenActionTrigger(null);
      }
    },
    [runStepActionAndRegisterCleanup, setActionPending, step],
  );

  const runStepActionAndContinue = useCallback(
    async (trigger: TourStepActionTrigger, next: () => void) => {
      try {
        await runStepActionWithVisibility(trigger);
      } finally {
        next();
      }
    },
    [runStepActionWithVisibility],
  );

  const handleSkip = useCallback(() => {
    if (isSkipRequestedRef.current) {
      return;
    }
    isSkipRequestedRef.current = true;

    void (async () => {
      const wasActionPending = isActionPendingRef.current;
      if (!wasActionPending) {
        setActionPending(true);
      }

      try {
        await onAllActionCleanups();
        await runStepActionAndRegisterCleanup("onSkip");
      } finally {
        if (!wasActionPending) {
          setActionPending(false);
        }
        stopTour(tourId);
      }
    })();
  }, [
    onAllActionCleanups,
    runStepActionAndRegisterCleanup,
    setActionPending,
    stopTour,
    tourId,
  ]);

  const handleBack = useCallback(() => {
    void runPendingFlow(async () => {
      await onStepActionCleanups(step.id);
      await runStepActionWithVisibility("onPrevious", { managePending: false });
      if (previousStepId) {
        onSuppressEnterAction(previousStepId);
      }
      previousStep(tourId);
    });
  }, [
    onStepActionCleanups,
    onSuppressEnterAction,
    previousStep,
    previousStepId,
    runPendingFlow,
    runStepActionWithVisibility,
    step.id,
    tourId,
  ]);

  const handleNext = useCallback(() => {
    const trigger = currentIndex === totalSteps - 1 ? "onComplete" : "onNext";
    void runStepActionAndContinue(trigger, () => nextStep(tourId));
  }, [currentIndex, nextStep, runStepActionAndContinue, totalSteps, tourId]);

  useEffect(() => {
    if (!step.completion) {
      return;
    }

    const handleTourEvent = (event: TourGuideEvent) => {
      const matchesTour = event.payload.tourId === tourId;
      const matchesEvent = event.type === step.completion?.event;
      const matchesPredicate = step.completion?.matches?.(event) ?? true;

      if (matchesTour && matchesEvent && matchesPredicate) {
        setIsStepComplete(true);
        onStepComplete(step.id);

        if (step.completion?.autoAdvance) {
          handleNext();
        }
      }
    };

    tourGuideBus.on("event", handleTourEvent);

    return () => tourGuideBus.off("event", handleTourEvent);
  }, [handleNext, onStepComplete, step.completion, step.id, tourId]);

  useEffect(() => {
    const handleActionVisibility = ({
      actionName,
      hidden,
      stepId,
      tourId: eventTourId,
    }: TourGuideStepActionVisibilityEvent) => {
      if (eventTourId !== tourId || stepId !== step.id) {
        return;
      }

      setHiddenStepActionName(hidden ? actionName : null);
    };

    tourGuideBus.on("actionVisibility", handleActionVisibility);

    return () => tourGuideBus.off("actionVisibility", handleActionVisibility);
  }, [step.id, tourId]);

  useIsomorphicLayoutEffect(() => {
    if (shouldSkipEnterAction) {
      onConsumeSkippedEnterAction(step.id);
      return;
    }

    void runStepActionWithVisibility("onEnter");
  }, [
    onConsumeSkippedEnterAction,
    runStepActionWithVisibility,
    shouldSkipEnterAction,
    step.id,
  ]);

  useEffect(() => {
    fallbackDialogRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleSkip();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleSkip]);

  if (hiddenActionTrigger || hiddenStepActionName) {
    return null;
  }

  if (!targetEl) {
    return (
      <FloatingPortal root={portalRoot}>
        <div
          aria-hidden="true"
          className="fixed inset-0 z-50 bg-slate-950/50"
        />
        <div
          aria-describedby={descriptionId}
          aria-labelledby={titleId}
          className={cn(
            "-translate-x-1/2 -translate-y-1/2 fixed top-1/2 left-1/2 z-[60] flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-3 rounded-lg border bg-popover p-4 text-popover-foreground shadow-lg outline-none",
            className,
          )}
          ref={fallbackDialogRef}
          role="dialog"
          tabIndex={-1}
        >
          <TourStepContent
            currentIndex={currentIndex}
            completionInstructions={step.completion?.instructions}
            descriptionId={descriptionId}
            isActionPending={isActionPending}
            isNextDisabled={!isStepComplete}
            onBack={handleBack}
            onNext={handleNext}
            onSkip={handleSkip}
            step={step}
            targetMissing
            titleId={titleId}
            totalSteps={totalSteps}
          />
        </div>
      </FloatingPortal>
    );
  }

  if (!rect) {
    return null;
  }

  const spotlightTop = Math.max(8, rect.top - spotlightPadding);
  const spotlightLeft = Math.max(8, rect.left - spotlightPadding);
  const spotlightWidth = rect.width + spotlightPadding * 2;
  const spotlightHeight = rect.height + spotlightPadding * 2;

  return (
    <FloatingPortal root={portalRoot}>
      <div
        aria-hidden="true"
        className="pointer-events-none fixed z-50 rounded-xl ring-2 ring-primary ring-offset-2 ring-offset-background transition-all duration-150"
        style={{
          boxShadow: "0 0 0 9999px rgb(15 23 42 / 0.54)",
          height: spotlightHeight,
          left: spotlightLeft,
          top: spotlightTop,
          width: spotlightWidth,
        }}
      />
      <FloatingFocusManager context={context} modal={false} returnFocus>
        <div
          aria-describedby={descriptionId}
          aria-labelledby={titleId}
          className={cn(
            "z-[60] flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-3 rounded-lg border bg-popover p-4 text-popover-foreground shadow-lg outline-none",
            className,
          )}
          ref={refs.setFloating}
          role="dialog"
          style={floatingStyles}
          tabIndex={-1}
        >
          <TourStepContent
            currentIndex={currentIndex}
            completionInstructions={step.completion?.instructions}
            descriptionId={descriptionId}
            isActionPending={isActionPending}
            isNextDisabled={!isStepComplete}
            onBack={handleBack}
            onNext={handleNext}
            onSkip={handleSkip}
            step={step}
            titleId={titleId}
            totalSteps={totalSteps}
          />
        </div>
      </FloatingFocusManager>
    </FloatingPortal>
  );
}

export function Tour({
  id,
  steps,
  className,
  defaultPlacement = "bottom",
  popoverOffset = 12,
  portalRoot,
  spotlightPadding = 8,
}: TourProps) {
  const registerTour = useTourGuideStore((state) => state.registerTour);
  const unregisterTour = useTourGuideStore((state) => state.unregisterTour);
  const activeStepId = useTourGuideStore(
    (state) => state.tours[id]?.activeStepId ?? null,
  );
  const completedStepIdsRef = useRef<Record<TourStepId, boolean>>({});
  const actionCleanupsRef = useRef<
    {
      stepId: TourStepId;
      cleanup: TourStepActionCleanup;
    }[]
  >([]);
  const suppressedEnterStepIdsRef = useRef<
    Partial<Record<TourStepId, boolean>>
  >({});
  const stepIds = useMemo(() => steps.map((step) => step.id), [steps]);
  const registeredSteps = useMemo(
    () =>
      steps.map((step) => ({
        id: step.id,
        actions: step.actions,
        completionEvent: step.completion?.event,
      })),
    [steps],
  );

  useIsomorphicLayoutEffect(() => {
    registerTour(id, registeredSteps);
  }, [id, registerTour, registeredSteps]);

  useIsomorphicLayoutEffect(() => {
    return () => unregisterTour(id);
  }, [id, unregisterTour]);

  const markStepComplete = useCallback((stepId: TourStepId) => {
    completedStepIdsRef.current[stepId] = true;
  }, []);
  const registerActionCleanup = useCallback(
    (stepId: TourStepId, cleanup: TourStepActionCleanup) => {
      actionCleanupsRef.current.push({ cleanup, stepId });
    },
    [],
  );
  const runStepActionCleanups = useCallback(async (stepId: TourStepId) => {
    const remainingCleanups: typeof actionCleanupsRef.current = [];
    const cleanupsToRun: typeof actionCleanupsRef.current = [];

    for (const entry of actionCleanupsRef.current) {
      if (entry.stepId === stepId) {
        cleanupsToRun.push(entry);
      } else {
        remainingCleanups.push(entry);
      }
    }

    actionCleanupsRef.current = remainingCleanups;

    for (const entry of cleanupsToRun.reverse()) {
      await entry.cleanup();
    }
  }, []);
  const runAllActionCleanups = useCallback(async () => {
    const cleanups = [...actionCleanupsRef.current].reverse();
    actionCleanupsRef.current = [];

    for (const entry of cleanups) {
      await entry.cleanup();
    }
  }, []);
  const suppressEnterAction = useCallback((stepId: TourStepId) => {
    suppressedEnterStepIdsRef.current[stepId] = true;
  }, []);
  const consumeSkippedEnterAction = useCallback((stepId: TourStepId) => {
    delete suppressedEnterStepIdsRef.current[stepId];
  }, []);

  const targetEl = useTourGuideStore(
    useCallback(
      (state) =>
        activeStepId
          ? (state.targets[TourGuideEntity.targetKey(id, activeStepId)] ?? null)
          : null,
      [activeStepId, id],
    ),
  );
  const currentIndex = activeStepId ? stepIds.indexOf(activeStepId) : -1;
  const activeStep = currentIndex >= 0 ? steps[currentIndex] : null;
  const previousStepId =
    currentIndex > 0 ? (stepIds[currentIndex - 1] ?? null) : null;
  const nextStepId =
    currentIndex >= 0 ? (stepIds[currentIndex + 1] ?? null) : null;

  if (!activeStep) {
    completedStepIdsRef.current = {};
    actionCleanupsRef.current = [];
    suppressedEnterStepIdsRef.current = {};
    return null;
  }

  return (
    <TourPopover
      key={`${id}:${activeStep.id}`}
      className={className}
      currentIndex={currentIndex}
      defaultPlacement={defaultPlacement}
      isStepInitiallyComplete={Boolean(
        completedStepIdsRef.current[activeStep.id],
      )}
      onAllActionCleanups={runAllActionCleanups}
      onConsumeSkippedEnterAction={consumeSkippedEnterAction}
      onRegisterActionCleanup={registerActionCleanup}
      onStepActionCleanups={runStepActionCleanups}
      onSuppressEnterAction={suppressEnterAction}
      onStepComplete={markStepComplete}
      nextStepId={nextStepId}
      popoverOffset={popoverOffset}
      portalRoot={portalRoot}
      previousStepId={previousStepId}
      shouldSkipEnterAction={Boolean(
        suppressedEnterStepIdsRef.current[activeStep.id],
      )}
      spotlightPadding={spotlightPadding}
      step={activeStep}
      targetEl={targetEl}
      totalSteps={steps.length}
      tourId={id}
    />
  );
}
